import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FaSearch, FaEdit, FaTrash, FaFileExcel } from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../api/axiosInstance";
import AddEditEmployeeTransfer from "./AddEditEmployeeTransfer";

const EmployeeTransferHistory = () => {

  /* ================= USER ================= */
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {}

  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [restMap, setRestMap] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* ================= FETCH ================= */

  const fetchEmployees = async () => {
    const res = await axiosMaster.get(
      `/EmployeeMaster/GetByGroup?groupId=${groupId}`
    );

    setEmployees(
      (res.data || []).map((e) => ({
        value: e.empId,
        label: `${e.empName} (${e.empMobileNo || ""})`,
      }))
    );
  };

  const fetchRestaurants = async () => {
    const res = await axiosMaster.get(
      `/RestaurantMaster/GetByGroup?groupId=${groupId}`
    );

    const map = {};
    (res.data || []).forEach((r) => {
      map[r.restId] = r.restaurantName;
    });

    setRestMap(map);
  };

  const fetchTransferHistory = async (empId) => {
    setLoading(true);

    const res = await axiosSalary.get(
      `/EmployeeTransferHistory/GetByEmployee?groupId=${groupId}&empId=${empId}`
    );

    setData(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchEmployees();
    fetchRestaurants();
  }, []);

  /* ================= SEARCH ================= */

  const filteredData = useMemo(() => {
    const term = search.toLowerCase();

    return data.filter((d) => {
      return (
        restMap[d.fromRestId]?.toLowerCase().includes(term) ||
        restMap[d.toRestId]?.toLowerCase().includes(term) ||
        d.transferReason?.toLowerCase().includes(term)
      );
    });
  }, [data, search, restMap]);

  /* ================= ACTIONS ================= */

  const handleEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this transfer?")) return;

    try {
      setDeletingId(row.id);

      await axiosSalary.delete(`/EmployeeTransferHistory/${row.id}`);

      fetchTransferHistory(selectedEmp.value);

    } catch {
      alert("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  /* ================= EXCEL ================= */

  const exportExcel = () => {
    if (!data.length) return;

    const rows = data.map((r) => ({
      Date: new Date(r.transferDate).toLocaleDateString(),
      From: restMap[r.fromRestId] || "",
      To: restMap[r.toRestId] || "",
      Reason: r.transferReason || "",
      Current: r.isCurrent ? "Yes" : "No",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "TransferHistory.csv";
    link.click();
  };

  /* ================= UI ================= */

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 border">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-3 mb-4">

        <h2 className="text-xl font-bold">Employee Transfer History</h2>

        <div className="flex gap-2 flex-wrap">

          {/* SELECT */}
          <div className="min-w-[250px]">
           
            <Select
              options={employees}
              value={selectedEmp}
              onChange={(opt) => {
                setSelectedEmp(opt);
                fetchTransferHistory(opt.value);
              }}
            />
          </div>

          {/* SEARCH */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border rounded-lg"
            />
          </div>

          {/* EXCEL */}
          <button
            onClick={exportExcel}
            className="bg-green-600 text-white px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <FaFileExcel /> Excel
          </button>

          {/* ADD */}
          <button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg"
          >
            🔁 Transfer
          </button>

        </div>
      </div>

      {/* TABLE */}
      <div className="hidden md:block border rounded-lg">

        <div className="max-h-[400px] overflow-y-auto">

          <table className="w-full text-sm">

            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="p-2 border">Date</th>
                <th className="p-2 border">From</th>
                <th className="p-2 border">To</th>
                <th className="p-2 border">Reason</th>
                <th className="p-2 border">Current</th>
                <th className="p-2 border">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center p-5">Loading...</td></tr>
              ) : filteredData.map((row) => (

                <tr key={row.id} className="even:bg-gray-50">

                  <td className="p-2 border">
                    {new Date(row.transferDate).toLocaleDateString()}
                  </td>

                  <td className="p-2 border">{restMap[row.fromRestId]}</td>
                  <td className="p-2 border">{restMap[row.toRestId]}</td>
                  <td className="p-2 border">{row.transferReason}</td>
                  <td className="p-2 border text-center">
                    {row.isCurrent ? "✅" : "-"}
                  </td>

                  <td className="p-2 border text-center">
                    <div className="flex justify-center gap-3">

                      <FaEdit
                        onClick={() => handleEdit(row)}
                        className="cursor-pointer text-blue-600"
                      />

                      <FaTrash
                        onClick={() => handleDelete(row)}
                        className={`cursor-pointer ${
                          deletingId === row.id ? "opacity-50" : "text-red-600"
                        }`}
                      />

                    </div>
                  </td>

                </tr>

              ))}
            </tbody>

          </table>
        </div>
      </div>

      {/* MOBILE */}
      <div className="md:hidden mt-3 space-y-3">
        {filteredData.map((row) => (
          <div key={row.id} className="border p-3 rounded-lg">

            <p><b>Date:</b> {new Date(row.transferDate).toLocaleDateString()}</p>
            <p><b>From:</b> {restMap[row.fromRestId]}</p>
            <p><b>To:</b> {restMap[row.toRestId]}</p>
            <p><b>Reason:</b> {row.transferReason}</p>

            <div className="flex justify-end gap-4 mt-2">
              <FaEdit onClick={()=>handleEdit(row)} className="text-blue-600"/>
              <FaTrash onClick={()=>handleDelete(row)} className="text-red-600"/>
            </div>

          </div>
        ))}
      </div>

      {/* MODAL */}
      {formOpen && (
        <AddEditEmployeeTransfer
          editingData={editing}
          selectedEmp={selectedEmp}
          onClose={() => setFormOpen(false)}
          onSuccess={() => fetchTransferHistory(selectedEmp.value)}
        />
      )}

    </div>
  );
};

export default EmployeeTransferHistory;